import { InputWithLabel } from '@adotel/components'
import {
  ApolloQueryResult,
  OperationVariables,
  useMutation
} from '@apollo/client'
import { Button, FormControlLabel, Grid, Switch } from '@material-ui/core'
import { useEffect, useState } from 'react'
import { toast } from 'react-toastify'

import WalletContentStructure from '../../components/WalletContentStructure'
import { UPDATE_WALLET_MUTATION } from '../../graphql/walletMutations'
import { DateObject } from '../../helpers/date'
import {
  addCommaToPrice,
  isValidCreditLimit,
  removeCommaFromPrice
} from '../../helpers/numbers'
import { getRoleFromStorage } from '../../helpers/userData'
import { getTranslate } from '../../localization'
import walletPagesRoleManagement from '../../staticRoleManagement/walletRoleManagement'
import { UpdateWalletInput, Wallet } from '../../types/wallet'
import { PaymentTermAssignmentRow } from '../../types/walletPaymentTerm'
import { PaymentTermAssignmentTable } from './paymentTermAssignments'
import useStyles from './styles/walletDetailContent.styles'

/**
 * The shared rule, narrowed for this screen's optional field.
 *
 * `UpdateWalletInput` types both limits as optional, so `undefined` is reachable here in a
 * way it never is server-side (Go's float64 parameter cannot be absent). Everything else
 * defers to `isValidCreditLimit` rather than restating it -- two copies of one domain rule
 * drift the moment the server's validation changes.
 */
const isSavableLimit = (value: number | undefined): boolean =>
  value !== undefined && isValidCreditLimit(value)

type WalletDetailContentProps = {
  wallet: Wallet
  assignment: PaymentTermAssignmentRow[]
  loadingPaymentTermAssignment: boolean
  coveringRange: DateObject
  refetch: (variables?: Partial<OperationVariables> | undefined) => Promise<
    ApolloQueryResult<{
      wallet: Wallet
    }>
  >
}

function WalletDetailContent({
  wallet,
  assignment,
  loadingPaymentTermAssignment,
  coveringRange,
  refetch
}: WalletDetailContentProps) {
  const classes = useStyles()
  const canEdit = walletPagesRoleManagement
    .find((item) => item.route === '/adopay/:page')
    ?.sections?.EditCreditor.rolesListWithWriteAccess?.includes(
      getRoleFromStorage()
    )
  const [input, setInput] = useState<UpdateWalletInput>({
    walletID: wallet.walletID,
    active: wallet.active,
    canUseCredit: wallet.canUseCredit,
    autoSettelmentEnabled: wallet.autoSettelmentEnabled,
    maxCredit: wallet.maxCredit,
    maxReservable: wallet.macReservable
  })

  const [updateWallet, { loading: loadingForUpdateWallet }] = useMutation(
    UPDATE_WALLET_MUTATION,
    {
      notifyOnNetworkStatusChange: true,
      onCompleted: (response) => {
        toast.success(response.updateWallet.message)
        refetch()
      },
      onError: (err) => {
        toast.error(err.message)
      }
    }
  )
  useEffect(() => {
    setInput({
      walletID: wallet.walletID,
      active: wallet.active,
      canUseCredit: wallet.canUseCredit,
      autoSettelmentEnabled: wallet.autoSettelmentEnabled,
      maxCredit: wallet.maxCredit,
      maxReservable: wallet.macReservable
    })
  }, [wallet])

  return (
    <>
      <div className={classes.tabSectionContent}>
        <WalletContentStructure
          title={'Entity Type'}
          value={wallet.entity.entityType}
        />
        <WalletContentStructure
          title={'Entity'}
          value={wallet.entity.entityName}
        />
        <WalletContentStructure
          title={'Wallet Type'}
          value={wallet.walletType}
          isBilateral={wallet.isBilateral}
          showIcon
        />
        <WalletContentStructure
          title={'Currency'}
          value={wallet.currency || ''}
        />
      </div>
      {!wallet.isBilateral && (
        <div className={classes.maxFields}>
          <div className={classes.inputs}>
            <InputWithLabel
              disabled={!canEdit}
              label={getTranslate('Max Credit')}
              autoComplete="off"
              fullWidth
              value={addCommaToPrice(input.maxCredit || '0')}
              onChange={(event) => {
                const commaLessValue = removeCommaFromPrice(
                  event.currentTarget.value
                )
                setInput({
                  ...input,
                  maxCredit: Number(commaLessValue)
                })
              }}
              inputSize="small"
            />
            <InputWithLabel
              label={getTranslate('Max Reservable')}
              autoComplete="off"
              fullWidth
              disabled={!canEdit}
              value={addCommaToPrice(input.maxReservable || '0')}
              onChange={(event) => {
                const commaLessValue = removeCommaFromPrice(
                  event.currentTarget.value
                )
                setInput({
                  ...input,
                  maxReservable: Number(commaLessValue)
                })
              }}
              inputSize="small"
            />
          </div>
          <div className={classes.switches}>
            <Grid item xs={12} sm={4}>
              <FormControlLabel
                control={
                  <Switch
                    disabled={!canEdit}
                    checked={input.active}
                    onChange={() =>
                      setInput({
                        ...input,
                        active: !input.active
                      })
                    }
                    color="primary"
                  />
                }
                label={getTranslate('Active')}
              />
            </Grid>
            <Grid item xs={12} sm={4}>
              <FormControlLabel
                control={
                  <Switch
                    disabled={!canEdit}
                    checked={input.canUseCredit}
                    onChange={() =>
                      setInput({
                        ...input,
                        canUseCredit: !input.canUseCredit
                      })
                    }
                    color="primary"
                  />
                }
                label={getTranslate('Can use credit')}
              />
            </Grid>
            <Grid item xs={12} sm={4}>
              <FormControlLabel
                control={
                  <Switch
                    disabled={!canEdit}
                    checked={input.autoSettelmentEnabled}
                    onChange={() =>
                      setInput({
                        ...input,
                        autoSettelmentEnabled: !input.autoSettelmentEnabled
                      })
                    }
                    color="primary"
                  />
                }
                label={getTranslate('Enable Auto Settlement')}
              />
            </Grid>
            <Button
              variant="contained"
              color="primary"
              className={classes.saveButton}
              // Uses the SHARED rule, not a local copy. `isValidCreditLimit` already exists
              // for exactly this: the creditor edit modal had the identical defect, and its
              // doc records the evidence -- 38 of the 47 creditors on stage carry zero
              // limits, so zero is the common case rather than an edge one.
              //
              // This previously read `!input.maxCredit || !input.maxReservable`, which
              // treats 0 as "not provided". A wallet whose limits are zero could never be
              // saved, and because this is the one Save button for the whole panel, the
              // Active / Can-use-credit / Auto-settlement switches were unsavable too.
              disabled={
                !isSavableLimit(input.maxCredit) ||
                !isSavableLimit(input.maxReservable) ||
                loadingForUpdateWallet ||
                !canEdit
              }
              onClick={() => {
                updateWallet({
                  variables: {
                    input: input
                  }
                })
              }}>
              {getTranslate('Save')}
            </Button>
          </div>
        </div>
      )}
      {!loadingPaymentTermAssignment && (
        <PaymentTermAssignmentTable
          assignments={assignment || []}
          coveringRange={coveringRange}
        />
      )}
    </>
  )
}

export default WalletDetailContent
